'use client';

import React, { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import { usePageLayout } from '../../../hooks/usePageLayout';
import useSnackbar from '../../../hooks/useSnackbar';
import { useMagnifierEnabled, FREE_AI_OCR_MAGNIFIER_ID } from '../../../hooks/useMagnifierEnabled';
import {
  Avatar,
  Box,
  Button,
  createTheme,
  Grid2,
  IconButton,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  boldMd,
  boldMdGray,
  boldMdWhite,
  boldSm,
  regularLgWhite,
  regularMdWhite,
  regularSm,
  baseTypography,
  basePalette,
  baseRadioStyle,
  baseSwitchStyle,
  baseMenuItemStyle,
  baseButtonHoverStyle,
  spHeaderHeight,
} from '../../../constants/location';
import {
  gray5,
  gray40,
  gray80,
  gray100,
  orange50,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
  white_overlay80,
} from '../../../constants/colors';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ReplayIcon from '@mui/icons-material/Replay';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import CustomTextfield from '../../uiParts/form/CustomTextfield';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import FileExportDialog from '../../uiParts/modal/FileExportDialog';
import LoadingModal from '../../uiParts/modal/LoadingModal';
import OcrLimitDialog from '../../uiParts/modal/OcrLimitDialog';
import PatternSelectModal from '../../uiParts/modal/PatternSelectModal';
import { useMediaQuery } from 'react-responsive';
import ImageOverlay, { FREE_OCR_LABEL_FOCUS_SUFFIX } from '../../uiParts/overlay/ImageOverlay';
import MagnifierToggle from '../../uiParts/overlay/MagnifierToggle';
import DeletedBadge from '../../uiParts/parts/DeletedBadge';
import {
  processingStatus,
  processingStatusLabel,
  registerStatus,
  resultStatus,
  exportFileType,
  exportEncoding,
  FREE_OCR_PATTERN_ID,
  PATTERN_ID_OTHER,
  PREVIEW_ZOOM,
  ocrExecutionType,
  zIndexLayer,
  GEMINI_MODEL_DEFAULT,
} from '../../../constants/enum';
import { isOperatorStaff } from '../../../utils/permission';
import { useDetailMode } from '../../../hooks/useDetailMode';
import OcrCostDisplay from '../../uiParts/parts/OcrCostDisplay';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { calculateOcrLimitInfo, getCreditRateByPlan } from '../../../utils/userUsageLimit';
import { executeProvisionalOcrUsage } from '../../../utils/provisionalOcrUsage';
import { handleReOcrPatternResult } from '../../../api/firebase/function/handleReOcrPatternResult';
import { getPattern } from '../../../api/firebase/firestore/pattern/read';
import { convertToUserFriendlyMessage } from '../../../utils/errorHandler';
import { PROXY_BANNER_HEIGHT, PROXY_DISPLAY_NAME } from '../../../constants/labels';
import { HELP_FAQ_CONSUMPTION_COUNT } from '../../../constants/externalUrls';
import { getDisplayName } from '../../../utils/displayName';
import { getOcrEngineDisplayName, getModelNameDisplay } from '../../../utils/ocrCostDisplay';
import HiStoryIcon from '../../assets/hi-story-icon.png';
import NoAccountIcon from '../../assets/Account circle.svg';
import { DocumentData, Timestamp, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { db } from '../../../api/firebase/config';
import { InputFileType } from '../../../types/input';
import { getResult } from '../../../api/firebase/firestore/result/read';
import EditIcon from '@mui/icons-material/Edit';
import AssigneeChangeDialog from '../../uiParts/modal/AssigneeChangeDialog';
import { getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import { assigneeLabels } from '../../../constants/assignee';
import { resolveAssigneeDisplay, type AssigneeOption } from '../../../utils/assigneeDisplay';
import { getFileUrl } from '../../../api/firebase/storage';
import { editResult } from '../../../api/firebase/firestore/result/update';
import {
  getOcrExecutions,
  OcrExecutionRecord,
} from '../../../api/firebase/firestore/result/ocrExecutions';
import { generateSearchTextFromResultData } from '../../../utils/generateSearchText';
import { createFileExportHistory } from '../../../api/firebase/firestore/fileExportHistory/create';
import { exportFile } from '../../../api/firebase/function/exportFileFunction';
import {
  generateFreeAiOcrVerticalRows,
  generateFreeAiOcrFileName,
  orderFreeOcrResultData,
  buildFreeOcrKeyOrder,
  type FreeOcrKeyOrderEntry,
} from '../../../utils/freeAiOcr/fileExportUtils';
import { isNoLabelEntry } from '../../../utils/freeAiOcr/noLabelKey';
import Encoding from 'encoding-japanese';
import Papa from 'papaparse';

// ページ番号を持たないファイルのソート順。番号付きファイルより後ろへ回すための番兵値。
const PAGE_ORDER_LAST = Number.MAX_SAFE_INTEGER;

// AIが返す任意のJSON構造を表す型
// boolean は項目名の有無を示す no_label フラグ（#3249）で使う
type OcrValue = string | number | boolean | null | OcrObject | OcrArray;
type OcrObject = { [key: string]: OcrValue };
type OcrArray = OcrValue[];
type FreeAiOcrResultData = OcrObject;

const styles = {
  backGround: {
    height: '100vh',
    width: '100%',
    display: 'flex',
  },

  leftContainer: {
    backgroundColor: gray5,
    '@media (min-width: 960px)': {
      overflowY: 'auto',
      height: '100vh',
    },
    '@media (max-width: 960px)': {
      height: 'auto',
      // 固定フッター(footerButtons)の実高分の余白。
      // 上padding16 + ボタン44 + gap12 + ボタン行44 + 下padding24 = 140px
      paddingBottom: '140px',
    },
  },

  rightContainer: {
    backgroundColor: gray100,
    height: '100vh',
    padding: '24px 16px',
    '@media (min-width: 960px)': {
      overflowY: 'auto',
    },
    '@media (max-width: 960px)': {
      height: 'auto',
      position: 'relative',
      paddingBottom: '72px',
      '&::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 0,
        left: 0,
        height: 'inherit',
        zIndex: -1,
      },
    },
  },

  leftHeader: {
    backgroundColor: white,
    padding: '18px 24px',
    height: 'auto',
    margin: '0px 2px 16px 0px',
    position: 'sticky' as const,
    top: 0,
    zIndex: zIndexLayer.stickyPageHeader,
    '@media (max-width: 960px)': {
      padding: '10px 12px',
      margin: '0px 0px 8px 0px',
      // SPヘッダー（fixed・高さ spHeaderHeight）の下に貼り付ける。
      // top: 0 のままだとヘッダーと同じ位置に重なり、zIndex も同値で後勝ちになるため
      // スクロール中にヘッダーを覆い隠してしまう。
      top: spHeaderHeight,
      zIndex: zIndexLayer.stickyPageHeaderSp,
    },
  },

  KeyboardArrowLeftButton: {
    color: gray80,
    padding: '10px 12px 10px 4px',
    height: '28px',
    '@media (max-width: 960px)': {
      height: '16px',
      width: '16px',
    },
  },

  KeyboardArrowRightButton: {
    color: gray80,
    padding: '10px 4px 10px 12px',
    height: '28px',
    '@media (max-width: 960px)': {
      height: '16px',
      width: '16px',
    },
  },

  closeButton: {
    color: gray80,
    padding: '10px 4px',
    height: '28px',
    minWidth: '32px',
  },

  staffName: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    margin: '0px 34px 16px 0px',
    gap: '4px',
    '@media (max-width: 960px)': {
      margin: '0px 16px 8px 0px',
    },
  },

  deliveryMainInfo: {
    backgroundColor: white,
    margin: '0px 16px 16px 16px',
    padding: '16px',
    '@media (max-width: 960px)': {
      margin: '0px 0px 8px 0px',
    },
  },

  deliveryProductInfo: {
    backgroundColor: white,
    margin: '0px 16px 40px 16px',
    padding: '16px',
    width: 'unset',
    boxShadow: 0,
    '@media (max-width: 960px)': {
      margin: '0px 0px 40px 0px',
    },
  },

  addButtom: {
    float: 'left',
    color: orange50,
  },

  addButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },

  removeButton: {
    color: orange50,
    height: '40px',
    width: '24px',
    padding: '8px 4px',
  },

  rightHeader: {
    backgroundColor: gray100,
    width: '100%',
  },

  cardMediaBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    overflowX: 'auto',
    '@media (max-width: 960px)': {
      margin: '16px 8px 32px',
    },
  },

  zoomControlBar: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: gray100,
  },

  zoomIconButton: {
    color: white,
    padding: '4px',
  },

  zoomPercentText: {
    color: white,
    minWidth: '44px',
    textAlign: 'center',
    fontSize: '0.75rem',
    userSelect: 'none',
  },

  openInNewButton: {
    color: white,
    minWidth: '32px',
  },

  downloadButton: {
    color: white,
    minWidth: '32px',
  },

  actionButtonsGrid: {
    '@media (max-width: 1148px)': {
      flexBasis: '100%',
      maxWidth: '100%',
    },
  },

  navButtonsGrid: {
    '@media (max-width: 1148px)': {
      flexBasis: '100%',
      maxWidth: '100%',
      justifyContent: 'flex-end',
      marginTop: '8px',
    },
    '@media (max-width: 960px)': {
      marginTop: '0px',
    },
  },

  buttonsDisplayNoneSp: {
    '@media (max-width: 960px)': {
      display: 'none',
    },
  },

  footerButtons: {
    backgroundColor: white_overlay80,
    padding: '16px 16px 24px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    right: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxSizing: 'border-box',
    zIndex: zIndexLayer.fixedFooter,
    '@media (min-width: 960px)': {
      display: 'none',
    },
  },

  footerTwoButtons: {
    width: '100%',
    display: 'flex',
    gap: '12px',
  },

  leftHeaderIconSize: {
    fontSize: '24px',
    '@media (max-width: 960px)': {
      fontSize: '16px',
    },
  },

  textCss: {
    ...boldMdGray,
    '@media (max-width: 960px)': {
      ...regularSm,
    },
  },

  fileTitleTextCss: {
    ...regularLgWhite,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '@media (max-width: 960px)': {
      ...regularMdWhite,
    },
  },

  rightHeaderIconSize: {
    fontSize: '32px',
    '@media (max-width: 960px)': {
      fontSize: '24px',
    },
  },

  draftButtonText: {
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
  },
};

// この画面はローカルテーマを持つため、App.tsx の共通設定を明示的に引き継ぐ必要がある。
// ThemeProvider はネストすると内側が優先され、palette / MuiSwitch を渡さないと
// ブランドのオレンジが失われてMUI既定の青に戻ってしまう（#3073）
const theme = createTheme({
  typography: baseTypography,
  palette: basePalette,
  components: {
    ...baseSwitchStyle,
    // プルダウンの項目はグレーで統一する（#3075）
    ...baseMenuItemStyle,
    // ボタン・アイコンボタンの既定ホバーはグレー（#3310）
    ...baseButtonHoverStyle,
    // disabled のラジオも選択色を残す（#3358）
    ...baseRadioStyle,
    MuiTableRow: {
      styleOverrides: {
        root: {
          minHeight: '56px',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1536,
    },
  },
});

/**
 * 既に使われている名前と重複しないキー名を返す（#3315）。
 *
 * result_data は map（オブジェクト）で保存するため、同じキーを2つ持てない。
 * そのまま書き込むと相手の値を上書きして消してしまうので、
 * 重複する場合は「小計(2)」のように連番を付けて退避させる。
 *
 * @param desired 入力された名前
 * @param currentKey 変更前の名前（自分自身との重複は無視する）
 * @param siblings 同じ階層にあるキーの一覧
 */
const resolveDuplicateKey = (desired: string, currentKey: string, siblings: string[]): string => {
  if (desired === currentKey) return desired;
  const used = new Set(siblings.filter(k => k !== currentKey));
  if (!used.has(desired)) return desired;
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${desired}(${suffix})`;
    if (!used.has(candidate)) return candidate;
  }
};

type ItemNameFieldProps = {
  /** 確定済みの名前（= オブジェクトのキー） */
  name: string;
  /** フォーカスが外れたときに呼ばれる。入力された名前をそのまま渡す */
  onCommit: (nextName: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  backgroundColor?: string;
  /** 名前が空など、保存できない状態のときに枠を赤くする（#3315） */
  error?: boolean;
};

/**
 * 項目名・列見出しの入力欄（#3315）。
 *
 * 入力中は編集内容をローカル state に持ち、フォーカスが外れたときに確定する。
 * 1文字ごとにキーを書き換える作りだと、全消ししたときに空キーが不正として弾かれて
 * 入力欄が巻き戻り、名前を入力し直せなくなっていた。
 * 確定を blur まで遅らせることで、途中で何を打っても邪魔されないようにする。
 */
const ItemNameField = ({
  name,
  onCommit,
  onFocus,
  onBlur,
  backgroundColor,
  error,
}: ItemNameFieldProps): ReactElement => {
  const [draft, setDraft] = useState<string>(name);

  // 確定済みの名前が外部で変わったとき（再読取・重複回避でのリネーム等）に追従する
  useEffect(() => {
    setDraft(name);
  }, [name]);

  return (
    <CustomTextfield
      value={draft}
      onChange={(e): void => setDraft(e.target.value)}
      onFocus={onFocus}
      onBlur={(): void => {
        onBlur?.();
        if (draft !== name) onCommit(draft);
      }}
      // 入力中も空なら赤くする。確定前に気付けるほうが直しやすい（#3315）
      error={error || draft.trim() === ''}
      inputWidth="100%"
      height="40px"
      spHeight="40px"
      fontSizeIs14px
      backgroundColor={backgroundColor}
    />
  );
};

const FreeAiOcrResult = (): ReactElement => {
  const { resultId } = useParams<{ resultId: string }>();
  const patternId = FREE_OCR_PATTERN_ID;
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const location = useLocation();
  const { user, company, isLoading } = useContext(AuthContext);
  const { isProxyMode, companyId: effectiveCompanyId } = useCompanyId();
  const { operatorId } = useContext(ProxyLoginContext);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const { sideBarCollapsed: _sideBarCollapsed, toggleSidebar: _toggleSidebar } = usePageLayout();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  // 運営スタッフ向けの詳細表示（コスト・処理時間）の可否（#3315）
  const { isDetailView } = useDetailMode();
  const [resultRecord, setResultRecord] = useState<DocumentData | undefined>(undefined);
  const [editData, setEditData] = useState<FreeAiOcrResultData>({});
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [fileExportDialogOpen, setFileExportDialogOpen] = useState<boolean>(false);
  const [isProcessingStatus, setIsProcessingStatus] = useState<number | null>(null);

  // ファイル表示用
  const [files, setFiles] = useState<InputFileType[]>([]);
  const [originalFileUrl, setOriginalFileUrl] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [focusItem, setFocusItem] = useState<string>('');
  const [createUser, setCreateUser] = useState<string>('');
  const [icon, setIcon] = useState<string>('');
  // 担当者（#2642）
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(PREVIEW_ZOOM.DEFAULT_LEVEL);
  // ルーペ（拡大表示）のON/OFF（自由読取専用キーでlocalStorageへ永続化・デフォルトON #3079）
  const { magnifierEnabled, toggleMagnifier } = useMagnifierEnabled(FREE_AI_OCR_MAGNIFIER_ID);
  // ルーペ（拡大表示）はマウス移動でしか位置が決まらず、タッチ操作では動かない。
  // SP では使えないコントロールを画面に残さないよう、トグル自体を非表示にする（#3224）。
  // localStorage の保存値は書き換えないため、PC に戻れば従来の設定がそのまま復活する。
  const isSpLayout = useMediaQuery({ query: '(max-width: 960px)' });
  const [baseRemainingCount, setBaseRemainingCount] = useState<number | null>(null);
  const [baseRemainingJpy, setBaseRemainingJpy] = useState<number | null>(null);
  const [isReOcrProcessing, setIsReOcrProcessing] = useState<boolean>(false);
  const [reOcrProgress, setReOcrProgress] = useState<number>(0);
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState<boolean>(false);
  const [reclassifyLoading, setReclassifyLoading] = useState<boolean>(false);
  const [limitDialogOpen, setLimitDialogOpen] = useState<boolean>(false);
  const [ocrExecutions, setOcrExecutions] = useState<OcrExecutionRecord[]>([]);

  // ナビゲーション
  const [nextResultId, setNextResultId] = useState<string | null>(null);
  const [prevResultId, setPrevResultId] = useState<string | null>(null);
  const [navigationResultIds, setNavigationResultIds] = useState<string[] | null>(null);

  const handleZoomIn = (): void => {
    setZoomLevel(prev =>
      Math.min(
        Math.round((prev + PREVIEW_ZOOM.STEP) * PREVIEW_ZOOM.PERCENT_BASE) /
          PREVIEW_ZOOM.PERCENT_BASE,
        PREVIEW_ZOOM.MAX_LEVEL,
      ),
    );
  };

  const handleZoomOut = (): void => {
    setZoomLevel(prev =>
      Math.max(
        Math.round((prev - PREVIEW_ZOOM.STEP) * PREVIEW_ZOOM.PERCENT_BASE) /
          PREVIEW_ZOOM.PERCENT_BASE,
        PREVIEW_ZOOM.MIN_LEVEL,
      ),
    );
  };

  const handleZoomReset = (): void => {
    setZoomLevel(PREVIEW_ZOOM.DEFAULT_LEVEL);
  };

  // location.state からナビゲーション情報を取得
  useEffect(() => {
    if (!company?.company_id || isProxyMode) return;
    const companyDocRef = doc(db, 'companies', company.company_id);
    const unsubscribe = onSnapshot(
      companyDocRef,
      snapshot => {
        const data = snapshot.data();
        if (data) {
          const ocrMonthlyBudget = data.ocr_monthly_budget ?? company.ocr_monthly_budget;
          const creditRate = getCreditRateByPlan(data.plan ?? company.plan);
          const currentMonthOcrCost = data.current_month_ocr_cost ?? 0;
          const baseRemaining = Math.floor((ocrMonthlyBudget - currentMonthOcrCost) / creditRate);
          setBaseRemainingCount(Math.max(0, baseRemaining));
          setBaseRemainingJpy(Math.max(0, ocrMonthlyBudget - currentMonthOcrCost));
        }
      },
      error => {
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(error);
      },
    );
    return () => unsubscribe();
  }, [company?.company_id, isProxyMode]);

  useEffect(() => {
    if (location.state?.navigationResultIds) {
      const navIds = location.state.navigationResultIds;
      setNavigationResultIds(Array.isArray(navIds) ? (navIds as string[]) : null);
    } else {
      setNavigationResultIds(null);
    }
  }, [location.state]);

  // 前後ナビゲーションIDを更新
  useEffect(() => {
    if (!resultId || !navigationResultIds || navigationResultIds.length === 0) {
      return;
    }
    const currentIndex = navigationResultIds.findIndex(id => id === resultId);
    if (currentIndex === -1) {
      setNextResultId(null);
      setPrevResultId(null);
      return;
    }
    setPrevResultId(currentIndex > 0 ? navigationResultIds[currentIndex - 1] : null);
    setNextResultId(
      currentIndex < navigationResultIds.length - 1 ? navigationResultIds[currentIndex + 1] : null,
    );
  }, [resultId, navigationResultIds]);

  const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = (): void => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = url;
    });
  };

  const updateFilesWithUrls = async (fileList: InputFileType[]): Promise<void> => {
    const updatedFiles = await Promise.all(
      fileList.map(async (file: InputFileType) => {
        let fileUrl = file.file_url;
        if (!fileUrl) {
          fileUrl = await getFileUrl(file);
        }
        try {
          const { width, height } = await getImageDimensions(fileUrl);
          return { ...file, file_url: fileUrl, file_width: width, file_height: height };
        } catch {
          return { ...file, file_url: fileUrl };
        }
      }),
    );
    setFiles(updatedFiles);
  };

  const fetchData = async (): Promise<void> => {
    if (!resultId) return;
    setIsDataReady(false);
    const result = await getResult(resultId);
    if (!result) {
      setErrorText('データが見つかりません');
      setSnackbarOpen(true);
      return;
    }
    setResultRecord(result);
    const rawResultData = result.result_data;
    const parsedResultData: FreeAiOcrResultData =
      typeof rawResultData === 'string'
        ? (JSON.parse(rawResultData) as FreeAiOcrResultData)
        : ((rawResultData ?? {}) as FreeAiOcrResultData);
    // フリーOCRのキー表示順を復元（result_data は map で保存されキー順が保証されないため）
    const orderedResultData = orderFreeOcrResultData(
      parsedResultData as Record<string, unknown>,
      result.free_ocr_key_order as FreeOcrKeyOrderEntry[] | undefined,
    ) as FreeAiOcrResultData;
    setEditData(orderedResultData);
    setIsProcessingStatus(result.processing_status ?? null);

    // 作成者情報
    setCreateUser(getDisplayName(result.user_name, result.is_proxy));
    setIcon(result.is_proxy ? HiStoryIcon : result.user_icon_url || '');

    // オリジナルファイルURL
    const originalFile = result.originalFiles?.[0];
    let origUrl = originalFile?.file_url;
    if (originalFile && !origUrl) {
      origUrl = await getFileUrl(originalFile);
    }
    setOriginalFileUrl(origUrl || '');
    setFileName(result.originalFiles?.[0]?.file_name || '');

    // 画像ファイル
    if (result.files && result.files.length > 0) {
      await updateFilesWithUrls(result.files as InputFileType[]);
    }

    // OCR実行履歴を取得
    try {
      const executions = await getOcrExecutions(resultId);
      setOcrExecutions(executions);
    } catch {
      // 履歴取得失敗はメイン処理に影響させない
    }

    setIsDataReady(true);
  };

  useEffect(() => {
    if (isLoading || !user) return;
    setLoading(true);
    fetchData()
      .catch(error => {
        setErrorText('データの取得に失敗しました');
        setSnackbarOpen(true);
        Sentry.captureException(error);
      })
      .finally(() => setLoading(false));
  }, [resultId, user, isLoading]);

  // 担当者候補リストをロード（#2642）
  useEffect(() => {
    if (isLoading || !user) return;
    const companyId = effectiveCompanyId || user.company_id;
    if (!companyId) {
      setAssigneeOptions([]);
      return;
    }
    void (async (): Promise<void> => {
      try {
        const users = await getUsersByCompanyId(companyId);
        const list: AssigneeOption[] = users
          .filter(u => u.register_status === registerStatus.finished)
          .map(u => ({
            uid: u.uid as string,
            name: (u.name as string) ?? '',
            iconUrl: (u.icon_url as string | undefined) ?? null,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'ja'));
        setAssigneeOptions(list);
      } catch {
        setAssigneeOptions([]);
      }
    })();
  }, [user, isLoading, effectiveCompanyId]);

  // 担当者変更成功/失敗時のハンドラ（#2642）
  const handleAssigneeChangeSuccess = (): void => {
    setInfoText(assigneeLabels.changeSuccess);
    setSnackbarOpen(true);
    void fetchData();
  };
  const handleAssigneeChangeError = (message: string): void => {
    setErrorText(message);
    setSnackbarOpen(true);
  };

  const handleSave = async (nextStatus?: number): Promise<void> => {
    if (!resultId || !user) return;
    // 項目名が空のままでは保存させない（#3315）。
    // 空文字キーは Firestore に保存できず、CSV出力でも見出しが消えるため、
    // 入力し直してもらう。項目名が無い行（"_1" 等）は対象外。
    const emptyKeyLabel = findEmptyKeyLabel(editData);
    if (emptyKeyLabel !== null) {
      setErrorText(
        emptyKeyLabel
          ? `「${emptyKeyLabel}」の中に項目名が空の項目があります。項目名を入力してください。`
          : '項目名が空の項目があります。項目名を入力してください。',
      );
      setSnackbarOpen(true);
      return;
    }
    setSaving(true);
    try {
      const fileNameForSearch = resultRecord?.originalFiles?.[0]?.file_name ?? '';
      const structuredText = generateSearchTextFromResultData(editData as Record<string, unknown>);
      const searchText = fileNameForSearch
        ? `${fileNameForSearch} ${structuredText}`
        : structuredText;

      // result_data は map で保存し、キー表示順は free_ocr_key_order で別管理する。
      // editData は表示順どおりにキーが並んでいるため、その順序（配列の列順含む）を保存する。
      const updatePayload: Record<string, unknown> = {
        result_data: editData,
        free_ocr_key_order: buildFreeOcrKeyOrder(editData as Record<string, unknown>),
        search_text: searchText,
        ...(nextStatus !== undefined ? { processing_status: nextStatus } : {}),
      };

      await editResult({ resultId, data: updatePayload, userId: user.uid });
      await fetchData();
      if (nextStatus === processingStatus.completed) {
        setInfoText(`${processingStatusLabel[processingStatus.completed]}にしました。`);
      } else if (nextStatus === processingStatus.processing) {
        setInfoText(`${processingStatusLabel[processingStatus.processing]}にしました。`);
      } else if (nextStatus === processingStatus.draft) {
        setInfoText('下書き保存しました。');
      } else {
        setInfoText('保存しました');
      }
      setSnackbarOpen(true);
    } catch (e) {
      Sentry.captureException(e);
      setErrorText('保存に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!resultId || !user) return;
    try {
      const resultRef = doc(db, 'results', resultId);
      await updateDoc(resultRef, {
        status: resultStatus.deleted,
        updated_at: Timestamp.now(),
        updated_by: user.uid,
      });
      setIsModalOpen(false);
      navigate('/ai-ocr/free');
    } catch {
      setErrorText('削除に失敗しました');
      setSnackbarOpen(true);
    }
  };

  const handlePrevResult = (): void => {
    if (!prevResultId) return;
    navigate(`/ai-ocr/free/result/${prevResultId}`, {
      state: { navigationResultIds },
    });
  };

  const handleNextResult = (): void => {
    if (!nextResultId) return;
    navigate(`/ai-ocr/free/result/${nextResultId}`, {
      state: { navigationResultIds },
    });
  };

  const openFile = (): void => {
    // #2971 ハイブリッド方式:
    // - 単一ページ: プレビューと同じ「回転後の画像」(files[0]) を開く
    // - 複数ページ: 全ページを閲覧できるよう従来どおり元ファイル(originalFileUrl)を開く（回転前）
    const singlePageRotatedUrl = files.length === 1 ? files[0]?.file_url : undefined;
    const targetUrl = singlePageRotatedUrl || originalFileUrl;
    if (!targetUrl) return;
    const fileNamePart = targetUrl.split('/').pop()?.split('?')[0];
    const fileExtension = fileNamePart?.split('.').pop()?.toLowerCase();
    switch (fileExtension) {
      case 'pdf':
        window.open(
          `https://docs.google.com/viewer?url=${encodeURIComponent(targetUrl)}&embedded=true`,
          '_blank',
        );
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
        window.open(targetUrl, '_blank');
        break;
      default:
        setErrorText('ファイルの表示に失敗しました。');
        break;
    }
  };

  const handleDownload = async (): Promise<void> => {
    try {
      const response = await fetch(originalFileUrl);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      setErrorText('ファイルのダウンロードに失敗しました。');
    }
  };

  const handleFileExport = async (
    fileType: (typeof exportFileType)[keyof typeof exportFileType],
    encoding: (typeof exportEncoding)[keyof typeof exportEncoding],
    _selectedFields: string[],
    _sortedPattern?: unknown,
    moveToOutputted?: boolean,
    _saveSetting?: boolean,
    _selectedFixedFields?: string[],
    _sortedUnifiedOrder?: string[],
    customFileName?: string,
  ): Promise<void> => {
    if (!user || !resultRecord || !resultId) {
      setErrorText('ユーザー情報が見つかりません');
      setSnackbarOpen(true);
      return;
    }

    try {
      const ext = fileType === exportFileType.excel ? '.xlsx' : '.csv';
      const trimmedCustomName = customFileName?.trim();
      const status =
        typeof resultRecord.processing_status === 'number'
          ? resultRecord.processing_status
          : processingStatus.completed;
      const tempFileName = trimmedCustomName
        ? `${trimmedCustomName}${ext}`
        : generateFreeAiOcrFileName(status, fileType, encoding);

      const historyId = await createFileExportHistory({
        pattern_id: patternId,
        pattern_name: 'フリーOCR',
        company_id: effectiveCompanyId ?? user.company_id ?? '',
        file_name: tempFileName,
        file_type: fileType === exportFileType.excel ? 'xlsx' : 'csv',
        created_by: user.uid,
        created_by_name: isProxyMode ? PROXY_DISPLAY_NAME : user.name,
        created_by_icon_url: isProxyMode ? '' : user.icon || '',
        is_proxy: isProxyMode || false,
      });

      // 履歴作成成功後に一覧へ遷移
      navigate('/ai-ocr/free/exports');

      // バックグラウンドでファイル生成・アップロード・ステータス更新
      (async (): Promise<void> => {
        try {
          const verticalRows = generateFreeAiOcrVerticalRows(resultRecord);

          let fileBlob: Blob;

          if (fileType === exportFileType.excel) {
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.aoa_to_sheet(verticalRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'データ');
            const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
            fileBlob = new Blob([buf], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
          } else {
            const csvString = Papa.unparse(verticalRows, { header: false });
            if (encoding === exportEncoding.shiftJis) {
              const sjisArray = Encoding.convert(
                Encoding.stringToCode(csvString),
                'SJIS',
                'UNICODE',
              );
              fileBlob = new Blob([new Uint8Array(sjisArray)], {
                type: 'text/csv;charset=shift_jis',
              });
            } else {
              const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
              fileBlob = new Blob([bom, new TextEncoder().encode(csvString)], {
                type: 'text/csv;charset=utf-8',
              });
            }
          }

          const fileContent = await new Promise<string>((resolve, reject): void => {
            const reader = new FileReader();
            reader.onloadend = (): void => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(fileBlob);
          });

          await exportFile({
            history_id: historyId,
            file_content: fileContent,
            file_size: fileBlob.size,
            encoding: encoding === exportEncoding.shiftJis ? 'sjis' : 'utf8',
          });

          if (moveToOutputted) {
            try {
              const resultRef = doc(db, 'results', resultId);
              await updateDoc(resultRef, {
                processing_status: processingStatus.outputted,
                updated_at: Timestamp.now(),
                updated_by: user.uid,
              });
            } catch {
              // ステータス更新失敗はファイル出力自体を失敗にしない
            }
          }
        } catch (exportError) {
          Sentry.captureException(exportError);
        }
      })();
    } catch (error) {
      Sentry.captureException(error);
      setErrorText('ファイル出力に失敗しました');
      setSnackbarOpen(true);
      throw error;
    }
  };

  // フィールド編集ハンドラ
  // パス配列でネストしたOcrObjectの値を更新するユーティリティ
  const setValueAtPath = (
    obj: OcrObject,
    path: (string | number)[],
    newVal: OcrValue,
  ): OcrObject => {
    if (path.length === 0) return obj;
    const [head, ...rest] = path;
    if (typeof head === 'number') return obj;
    if (rest.length === 0) return { ...obj, [head]: newVal };
    const child = obj[head];
    if (Array.isArray(child)) {
      const [idx, ...deepRest] = rest;
      const arr = [...child];
      if (typeof idx === 'number') {
        const item = arr[idx];
        arr[idx] =
          deepRest.length === 0
            ? newVal
            : typeof item === 'object' && item !== null && !Array.isArray(item)
              ? setValueAtPath(item as OcrObject, deepRest, newVal)
              : newVal;
      }
      return { ...obj, [head]: arr };
    }
    if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
      return { ...obj, [head]: setValueAtPath(child as OcrObject, rest, newVal) };
    }
    return { ...obj, [head]: newVal };
  };

  // パス配列でキー名を変更するユーティリティ
  const renameKeyAtPath = (
    obj: OcrObject,
    path: (string | number)[],
    newKey: string,
  ): OcrObject => {
    if (path.length === 0) return obj;
    const [head, ...rest] = path;
    if (typeof head === 'number') return obj;
    if (rest.length === 0) {
      // 重複の回避は呼び出し側で resolveDuplicateKey を通してから渡す（#3315）。
      // ここでそのまま書き込むと、同名キーが相手の値を上書きして消してしまう。
      const newObj: OcrObject = {};
      for (const [k, v] of Object.entries(obj)) {
        newObj[k === head ? newKey : k] = v;
      }
      return newObj;
    }
    const child = obj[head];
    if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
      return { ...obj, [head]: renameKeyAtPath(child as OcrObject, rest, newKey) };
    }
    return obj;
  };

  // 配列に行を追加するユーティリティ
  const addRowAtPath = (obj: OcrObject, path: (string | number)[]): OcrObject => {
    if (path.length === 0) return obj;
    const [head, ...rest] = path;
    if (typeof head === 'number') return obj;
    if (rest.length === 0) {
      const arr = obj[head];
      if (!Array.isArray(arr)) return obj;
      const template =
        arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null ? arr[0] : null;
      const emptyRow: OcrValue = template
        ? Object.fromEntries(Object.keys(template as OcrObject).map(k => [k, '']))
        : '';
      return { ...obj, [head]: [...arr, emptyRow] };
    }
    const child = obj[head];
    if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
      return { ...obj, [head]: addRowAtPath(child as OcrObject, rest) };
    }
    return obj;
  };

  // 配列から行を削除するユーティリティ
  const removeRowAtPath = (obj: OcrObject, path: (string | number)[], idx: number): OcrObject => {
    if (path.length === 0) return obj;
    const [head, ...rest] = path;
    if (typeof head === 'number') return obj;
    if (rest.length === 0) {
      const arr = obj[head];
      if (!Array.isArray(arr)) return obj;
      return { ...obj, [head]: arr.filter((_, i) => i !== idx) };
    }
    const child = obj[head];
    if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
      return { ...obj, [head]: removeRowAtPath(child as OcrObject, rest, idx) };
    }
    return obj;
  };

  // 配列内の全行で列名を一括変更するユーティリティ
  const renameColumnInArray = (
    obj: OcrObject,
    path: (string | number)[],
    oldCol: string,
    newCol: string,
  ): OcrObject => {
    if (path.length === 0) return obj;
    const [head, ...rest] = path;
    if (typeof head === 'number') return obj;
    if (rest.length === 0) {
      const arr = obj[head];
      if (!Array.isArray(arr)) return obj;
      const newArr = arr.map(item => {
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          const row = item as OcrObject;
          const newRow: OcrObject = {};
          for (const [k, v] of Object.entries(row)) {
            newRow[k === oldCol ? newCol : k] = v;
          }
          return newRow;
        }
        return item;
      });
      return { ...obj, [head]: newArr };
    }
    const child = obj[head];
    if (typeof child === 'object' && child !== null && !Array.isArray(child)) {
      return { ...obj, [head]: renameColumnInArray(child as OcrObject, rest, oldCol, newCol) };
    }
    return obj;
  };

  const INTERNAL_KEYS = new Set([
    'id',
    'label_id',
    'bounding_boxes',
    'label_bounding_boxes',
    'page',
    'label_page',
    'confidence',
    'no_label',
  ]);

  // mergeBoundingBoxes後の { value, id, bounding_boxes, page, confidence } 形式を検知する
  const isValueNode = (node: OcrValue): node is OcrObject =>
    typeof node === 'object' &&
    node !== null &&
    !Array.isArray(node) &&
    'value' in (node as OcrObject);

  /**
   * 項目名が空の項目を探す（#3315）。
   *
   * 見つかったらその項目が属するかたまり・表の名前を返す（最上位なら空文字）。
   * 無ければ null を返す。保存前の入力チェックに使う。
   * 明細（配列）の列見出しも空にできるため、そちらも見る。
   */
  const findEmptyKeyLabel = (node: OcrObject, sectionLabel = ''): string | null => {
    for (const [k, v] of Object.entries(node)) {
      if (INTERNAL_KEYS.has(k)) continue;
      // 文書に項目名が無い行（"_1" 等）はもともと項目名を持たないため対象外
      if (isNoLabelEntry(k, v)) continue;
      if (k.trim() === '') return sectionLabel;
      // 明細（表）は先頭行のキーが列見出しになる
      if (Array.isArray(v) && v.length > 0) {
        const firstRow = v[0];
        if (typeof firstRow === 'object' && firstRow !== null && !Array.isArray(firstRow)) {
          const hasEmptyColumn = Object.keys(firstRow as OcrObject).some(
            col => !INTERNAL_KEYS.has(col) && col.trim() === '',
          );
          if (hasEmptyColumn) return k;
        }
        continue;
      }
      // かたまり（値ノードでも配列でもないオブジェクト）は中も見る
      if (typeof v === 'object' && v !== null && !Array.isArray(v) && !isValueNode(v)) {
        const nested = findEmptyKeyLabel(v as OcrObject, k);
        if (nested !== null) return nested;
      }
    }
    return null;
  };

  // path配列からImageOverlayが期待するfocusKey文字列を生成する
  // ImageOverlay 側の buildFreeOcrFocusKey と同じ規則にする必要がある
  // 単一値: ['項目名', 'value'] → '項目名'
  // 配列内: ['親項目名', 0, '子項目名', 'value'] → '親項目名-0-子項目名'
  // かたまり内: ['送信先', 'FAX', 'value'] → '送信先.FAX'
  const buildFocusKey = (path: (string | number)[]): string => {
    const p = path[path.length - 1] === 'value' ? path.slice(0, -1) : path;
    if (p.length === 1) return String(p[0]);
    if (p.length === 3 && typeof p[1] === 'number') return `${p[0]}-${p[1]}-${p[2]}`;
    return p.join('.');
  };

  // 汎用フィールドレンダラー
  const renderOcrNode = (
    node: OcrValue,
    path: (string | number)[],
    depth: number,
  ): React.ReactNode => {
    // { value, id, bounding_boxes, ... } 形式 → value だけを編集フィールドとして表示
    if (isValueNode(node)) {
      const obj = node as OcrObject;
      const strVal = obj.value != null ? String(obj.value) : '';
      const isLong = strVal.length > 50 || strVal.includes('\n');
      return (
        <CustomTextfield
          value={strVal}
          onChange={(e): void =>
            setEditData(prev => setValueAtPath(prev, [...path, 'value'], e.target.value))
          }
          onFocus={(): void => setFocusItem(buildFocusKey(path))}
          onBlur={(): void => setFocusItem('')}
          inputWidth="100%"
          height={isLong ? 'auto' : '40px'}
          spHeight={isLong ? 'auto' : '40px'}
          fontSizeIs14px
          {...(isLong && { multiline: true, minRows: 3 })}
        />
      );
    }

    // スカラー値
    if (node === null || typeof node === 'string' || typeof node === 'number') {
      const strVal = node != null ? String(node) : '';
      const isLong = strVal.length > 50 || strVal.includes('\n');
      return (
        <CustomTextfield
          value={strVal}
          onChange={(e): void => setEditData(prev => setValueAtPath(prev, path, e.target.value))}
          onFocus={(): void => setFocusItem(buildFocusKey(path))}
          onBlur={(): void => setFocusItem('')}
          inputWidth="100%"
          height={isLong ? 'auto' : '40px'}
          spHeight={isLong ? 'auto' : '40px'}
          fontSizeIs14px
          {...(isLong && { multiline: true, minRows: 3 })}
        />
      );
    }

    // 配列
    if (Array.isArray(node)) {
      const isObjectArray =
        node.length > 0 &&
        typeof node[0] === 'object' &&
        node[0] !== null &&
        !Array.isArray(node[0]);

      if (isObjectArray) {
        const allColumns = Object.keys(node[0] as OcrObject);
        const columns = allColumns.filter(k => !INTERNAL_KEYS.has(k));
        return (
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: '400px' }}>
              {/* ヘッダー */}
              <Box
                sx={{
                  display: 'flex',
                  borderBottom: `2px solid ${gray40}`,
                }}
              >
                {columns.map((col, ci) => (
                  <Box key={ci} sx={{ flex: 1, padding: '4px' }}>
                    <ItemNameField
                      name={col}
                      // 確定時にだけ列名を書き換える（#3315）。入力中に書き換えると
                      // 全消しした時点で弾かれ、名前を入力し直せなくなる。
                      onCommit={(nextCol): void =>
                        setEditData(prev =>
                          renameColumnInArray(
                            prev,
                            path,
                            col,
                            resolveDuplicateKey(nextCol, col, columns),
                          ),
                        )
                      }
                      // 列見出しにフォーカスしたら、原本の列見出しをハイライトする（#3315）。
                      // 列見出しは全行で共通なので、代表として先頭行のラベル枠を指す。
                      // 明細セルに label_id が無い結果では光る枠が無いが、
                      // 従来もフォーカス時に何も起きなかったため挙動は変わらない。
                      onFocus={(): void =>
                        setFocusItem(
                          `${buildFocusKey([...path, 0, col])}${FREE_OCR_LABEL_FOCUS_SUFFIX}`,
                        )
                      }
                      onBlur={(): void => setFocusItem('')}
                      backgroundColor="#f5f5f5"
                    />
                  </Box>
                ))}
                <Box sx={{ width: '32px', flexShrink: 0 }} />
              </Box>
              {/* 行 */}
              {(node as OcrObject[]).map((row, ri) => (
                <Box
                  key={ri}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `1px solid ${gray40}`,
                  }}
                >
                  {columns.map((col, ci) => (
                    <Box key={ci} sx={{ flex: 1, padding: '4px' }}>
                      {renderOcrNode((row as OcrObject)[col] ?? '', [...path, ri, col], depth + 1)}
                    </Box>
                  ))}
                  <Box sx={{ width: '32px', flexShrink: 0, textAlign: 'center' }}>
                    <IconButton
                      sx={{ padding: 0 }}
                      onClick={(): void => setEditData(prev => removeRowAtPath(prev, path, ri))}
                    >
                      <RemoveCircleOutlineIcon sx={styles.removeButton} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={(): void => setEditData(prev => addRowAtPath(prev, path))}
              sx={styles.addButtom}
            >
              <Typography sx={boldSm}>行を追加</Typography>
            </Button>
          </Box>
        );
      }

      // スカラー配列
      return (
        <Box>
          {node.map((item, ri) => (
            <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {/* フレックスアイテムも min-width の既定が auto のため、明示的に 0 にする */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {renderOcrNode(item, [...path, ri], depth + 1)}
              </Box>
              <IconButton
                sx={{ padding: 0 }}
                onClick={(): void => setEditData(prev => removeRowAtPath(prev, path, ri))}
              >
                <RemoveCircleOutlineIcon sx={styles.removeButton} />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={(): void => setEditData(prev => addRowAtPath(prev, path))}
            sx={styles.addButtom}
          >
            <Typography sx={boldSm}>行を追加</Typography>
          </Button>
        </Box>
      );
    }

    // オブジェクト（ネスト）
    if (typeof node === 'object') {
      return (
        <Box
          sx={{
            pl: depth > 0 ? 2 : 0,
            borderLeft: depth > 0 ? `2px solid ${gray40}` : 'none',
            mt: depth > 0 ? 1 : 0,
          }}
        >
          {Object.entries(node as OcrObject)
            .filter(([k]) => !INTERNAL_KEYS.has(k))
            .map(([k, v], entryIndex) => {
              // 文書に項目名が無い行（表題・ロゴ・注記など）はキーが "_1" のような
              // 内部的な連番になる。項目名欄には表示せず、値だけを見せる（#3249）。
              // ユーザーが項目名を全消しした場合（キーが空文字）は入力欄を残す。
              // 消えると入力し直せなくなるため、連番キー・no_label でのみ判定する（#3315）。
              const hasNoLabel = isNoLabelEntry(k, v);
              return (
                // key に項目名 k を使うと、項目名編集で 1 文字入力するたびにキーが変わって
                // Box が再マウントされフォーカスが外れる。安定した index を key に使う
                <Box key={entryIndex} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      alignItems: 'flex-start',
                      // 値側は minmax(0, 1fr) にする。1fr は既定で minmax(auto, 1fr) となり
                      // トラックの最小幅が min-content になるため、列数の多い明細を入れると
                      // グリッドごとページ全体が横に広がり、内側の overflowX: auto が効かなくなる。
                      // SP は項目名 200px と値を横に並べるだけの幅が無い。入力欄は input 既定
                      // サイズ由来の最小幅（約200px）を持つため、残り幅に収まらずはみ出す。
                      // ネストが深いほど左インデントで幅が減り、はみ出し量も増えるため縦積みにする。
                      gridTemplateColumns: '200px minmax(0, 1fr)',
                      '@media (max-width: 960px)': {
                        gridTemplateColumns: 'minmax(0, 1fr)',
                      },
                    }}
                  >
                    {hasNoLabel ? (
                      // 項目名が無い行は入力欄を置かず空セルにする（キーは内部連番のため見せない）
                      <Box />
                    ) : (
                      <ItemNameField
                        name={k}
                        // 確定時にだけキーを書き換える（#3315）。入力中に書き換えると
                        // 全消しした時点で空キーになり、名前を入力し直せなくなる。
                        onCommit={(nextName): void =>
                          setEditData(prev =>
                            renameKeyAtPath(
                              prev,
                              [...path, k],
                              resolveDuplicateKey(nextName, k, Object.keys(node as OcrObject)),
                            ),
                          )
                        }
                        // 項目名欄にフォーカスしたら、原本の項目名（ラベル）だけを
                        // ハイライトしてそこへスクロールする（#3315）。
                        // 値の枠とはキーを分ける。明細は列見出しが全行で共通のため、
                        // 同じキーにすると値を押しても見出しへ飛んでしまう。
                        onFocus={(): void =>
                          setFocusItem(
                            `${buildFocusKey([...path, k])}${FREE_OCR_LABEL_FOCUS_SUFFIX}`,
                          )
                        }
                        onBlur={(): void => setFocusItem('')}
                        backgroundColor="#f5f5f5"
                      />
                    )}
                    {/* グリッドアイテムの min-width は既定 auto（＝中身の最小幅）のため、
                      トラックを minmax(0, 1fr) にしてもアイテム側が縮まずはみ出す。 */}
                    <Box sx={{ minWidth: 0 }}>{renderOcrNode(v, [...path, k], depth + 1)}</Box>
                  </Box>
                </Box>
              );
            })}
        </Box>
      );
    }

    return null;
  };

  const saveButtonStyles = {
    buttonChange:
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
        ? { ...secondaryButtonBackgroundColor }
        : isProcessingStatus === processingStatus.draft ||
            isProcessingStatus === processingStatus.processing
          ? { ...primaryButtonBackgroundColor }
          : {},
    textColorChange:
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
        ? { ...boldMd }
        : isProcessingStatus === processingStatus.draft ||
            isProcessingStatus === processingStatus.processing
          ? { ...boldMdWhite }
          : {},
  };

  const handleReOcr = (): void => {
    if (!resultId) return;
    if (company && !isProxyMode) {
      const pageCount =
        (resultRecord?.token_usage as Record<string, number> | undefined)?.page_count ||
        resultRecord?.original_files?.length ||
        1;
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      if (limitInfo.totalRemainingCount < pageCount) {
        setLimitDialogOpen(true);
        return;
      }
    }
    void executeReOcr();
  };

  const executeReOcr = async (): Promise<void> => {
    if (!resultId) return;
    setReOcrProgress(0);
    setIsReOcrProcessing(true);

    let feProvisionalCreditAmount = 0;
    let provisionalDeducted = false;
    const reOcrGeminiModel = GEMINI_MODEL_DEFAULT;

    if (company) {
      try {
        const pageCount =
          (resultRecord?.token_usage as Record<string, number> | undefined)?.page_count ||
          resultRecord?.original_files?.length ||
          1;
        const provisionalResult = await executeProvisionalOcrUsage(
          company,
          pageCount,
          baseRemainingCount,
          isProxyMode,
          reOcrGeminiModel,
          false,
          1,
          [pageCount],
          baseRemainingJpy,
        );
        feProvisionalCreditAmount = provisionalResult.feProvisionalCreditAmount;
        provisionalDeducted = true;
      } catch (error) {
        setIsReOcrProcessing(false);
        setErrorText(convertToUserFriendlyMessage(error));
        return;
      }
    }

    const progressInterval = setInterval(() => {
      setReOcrProgress(prev => {
        if (prev >= 90) return prev;
        return prev + (90 - prev) * 0.08;
      });
    }, 500);

    try {
      const pageCount =
        (resultRecord?.token_usage as Record<string, number> | undefined)?.page_count ||
        resultRecord?.original_files?.length ||
        1;
      await handleReOcrPatternResult({
        resultId,
        patternId,
        itemList: [],
        geminiModel: reOcrGeminiModel,
        provisionalDeducted,
        feProvisionalCreditAmount,
        feProvisionalUsageCount: pageCount,
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
      });
      clearInterval(progressInterval);
      setReOcrProgress(100);
      setTimeout(async () => {
        await fetchData();
        setIsReOcrProcessing(false);
        setReOcrProgress(0);
      }, 800);
    } catch (error) {
      clearInterval(progressInterval);
      setIsReOcrProcessing(false);
      setReOcrProgress(0);
      setErrorText(convertToUserFriendlyMessage(error));
    }
  };

  const handleReclassifySelect = (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): void => {
    if (!resultId) return;
    if (!isProxyMode && newPatternId !== PATTERN_ID_OTHER && company) {
      const pageCount =
        (resultRecord?.token_usage as Record<string, number> | undefined)?.page_count ||
        resultRecord?.original_files?.length ||
        1;
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      if (limitInfo.totalRemainingCount < pageCount) {
        setReclassifyModalOpen(false);
        setLimitDialogOpen(true);
        return;
      }
    }
    void executeReclassify(newPatternId, itemList);
  };

  const executeReclassify = async (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): Promise<void> => {
    if (!resultId) return;
    setReclassifyModalOpen(false);
    setReclassifyLoading(true);
    setReOcrProgress(0);
    setIsReOcrProcessing(true);

    let feProvisionalCreditAmount = 0;
    let provisionalDeducted = false;

    try {
      const reclassifyPattern =
        newPatternId !== PATTERN_ID_OTHER ? await getPattern(newPatternId).catch(() => null) : null;
      const reclassifyGeminiModel = reclassifyPattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;

      if (newPatternId !== PATTERN_ID_OTHER && company) {
        const pageCount =
          (resultRecord?.token_usage as Record<string, number> | undefined)?.page_count ||
          resultRecord?.original_files?.length ||
          1;
        const provisionalResult = await executeProvisionalOcrUsage(
          company,
          pageCount,
          baseRemainingCount,
          isProxyMode,
          reclassifyGeminiModel,
          false,
          1,
          [pageCount],
          baseRemainingJpy,
        );
        feProvisionalCreditAmount = provisionalResult.feProvisionalCreditAmount;
        provisionalDeducted = true;
      }

      const progressInterval = setInterval(() => {
        setReOcrProgress(prev => {
          if (prev >= 90) return prev;
          return prev + (90 - prev) * 0.08;
        });
      }, 500);

      try {
        const pageCount =
          (resultRecord?.token_usage as Record<string, number> | undefined)?.page_count ||
          resultRecord?.original_files?.length ||
          1;
        await handleReOcrPatternResult({
          resultId,
          patternId: newPatternId,
          itemList,
          isReclassification: true,
          provisionalDeducted,
          feProvisionalCreditAmount,
          feProvisionalUsageCount: pageCount,
          isProxy: isProxyMode,
          operatorId: isProxyMode ? operatorId || undefined : undefined,
          geminiModel: reclassifyGeminiModel,
        });
        clearInterval(progressInterval);
        setReOcrProgress(100);
        setTimeout(() => {
          setIsReOcrProcessing(false);
          setReOcrProgress(0);
          navigate(`/ai-ocr/pattern/${newPatternId}/result/${resultId}`, { replace: true });
        }, 800);
      } catch (error) {
        clearInterval(progressInterval);
        setIsReOcrProcessing(false);
        setReOcrProgress(0);
        navigate(`/ai-ocr/pattern/${newPatternId}`, { replace: true });
        throw error;
      }
    } catch (error) {
      setErrorText(convertToUserFriendlyMessage(error));
    } finally {
      setReclassifyLoading(false);
    }
  };

  const buttonText = (): string => {
    if (isProcessingStatus === processingStatus.processing) {
      return `${processingStatusLabel[processingStatus.completed]}にする`;
    } else if (
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
    ) {
      return `${processingStatusLabel[processingStatus.processing]}にする`;
    }
    return `${processingStatusLabel[processingStatus.completed]}にする`;
  };

  // ページ番号順に並べたプレビュー用ファイル。
  // JSX 内で files.sort() を呼ぶと state 配列を破壊的に変更してしまうため、コピーしてからソートする
  const sortedFiles = useMemo<InputFileType[]>(
    () =>
      [...files].sort((a: InputFileType, b: InputFileType) => {
        const pageA = a.page || PAGE_ORDER_LAST;
        const pageB = b.page || PAGE_ORDER_LAST;
        return pageA - pageB;
      }),
    [files],
  );

  const handleProcessButton = (): void => {
    if (
      isProcessingStatus === processingStatus.draft ||
      isProcessingStatus === processingStatus.processing
    ) {
      void handleSave(processingStatus.completed);
    } else if (
      isProcessingStatus === processingStatus.completed ||
      isProcessingStatus === processingStatus.outputted
    ) {
      void handleSave(processingStatus.processing);
    }
  };

  if (!isDataReady) {
    return <CustomLoading loading={true} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <MetaTitle customTitle="フリーOCR 結果" />
      <SideBar pcIsNone />
      <CustomLoading loading={loading || saving} />
      <Grid2
        container
        sx={{
          ...styles.backGround,
          ...(isProxyMode && {
            height: `calc(100vh - ${PROXY_BANNER_HEIGHT}px)`,
            marginTop: `${PROXY_BANNER_HEIGHT}px`,
          }),
        }}
      >
        {/* 画像領域（左側） */}
        <Grid2
          size={{ xs: 12, md: 5 }}
          sx={{
            ...styles.rightContainer,
            ...(isProxyMode && {
              height: `calc(100vh - ${PROXY_BANNER_HEIGHT}px)`,
            }),
          }}
          container
          flexDirection={'row'}
          spacing={'18px'}
          alignContent={'flex-start'}
        >
          <Grid2 container sx={styles.rightHeader} spacing={1}>
            <Grid2 size={10} display="flex" justifyContent="start" alignItems="center" gap={1}>
              <Typography sx={styles.fileTitleTextCss} textAlign="left">
                {fileName}
              </Typography>
              {resultRecord?.status === resultStatus.deleted && <DeletedBadge />}
            </Grid2>
            <Grid2 size={2} display="flex" justifyContent="end" alignItems="center">
              <Button sx={styles.openInNewButton} onClick={openFile}>
                <OpenInNewIcon sx={styles.rightHeaderIconSize} />
              </Button>
              <Button sx={styles.downloadButton} onClick={handleDownload}>
                <DownloadIcon sx={styles.rightHeaderIconSize} />
              </Button>
            </Grid2>
          </Grid2>
          {Object.keys(editData).length > 0 && files && files.length > 0 && (
            <>
              <Box sx={styles.zoomControlBar}>
                {/* ルーペ（拡大表示）のON/OFF切り替え（一番左に配置・共通コンポーネント #3079）。
                    SP では機能しないため表示しない（#3224） */}
                {!isSpLayout && (
                  <MagnifierToggle checked={magnifierEnabled} onChange={toggleMagnifier} />
                )}
                <Tooltip title="縮小">
                  <span>
                    <IconButton
                      sx={styles.zoomIconButton}
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= PREVIEW_ZOOM.MIN_LEVEL}
                      size="small"
                    >
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography sx={styles.zoomPercentText}>
                  {Math.round(zoomLevel * PREVIEW_ZOOM.PERCENT_BASE)}%
                </Typography>
                <Tooltip title="拡大">
                  <span>
                    <IconButton
                      sx={styles.zoomIconButton}
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= PREVIEW_ZOOM.MAX_LEVEL}
                      size="small"
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="拡大率リセット">
                  <span>
                    <IconButton sx={styles.zoomIconButton} onClick={handleZoomReset} size="small">
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Box sx={styles.cardMediaBox}>
                {sortedFiles.map((file: InputFileType, index: number) => {
                  if (!file.file_url || !file.file_width || !file.file_height) return null;
                  const currentPage = file.page || index + 1;
                  return (
                    <ImageOverlay
                      key={index}
                      magnifierEnabled={magnifierEnabled}
                      imageUrl={file.file_url}
                      result={editData as unknown as Parameters<typeof ImageOverlay>[0]['result']}
                      imageWidth={file.file_width}
                      imageHeight={file.file_height}
                      focusedField={focusItem}
                      forDemo={true}
                      currentPage={currentPage}
                      itemList={[]}
                      showConfidence={isOperatorStaff(user?.role)}
                      zoomLevel={zoomLevel}
                    />
                  );
                })}
              </Box>
            </>
          )}
        </Grid2>

        {/* フィールド領域（右側） */}
        <Grid2
          size={{ xs: 12, md: 7 }}
          sx={{
            ...styles.leftContainer,
            ...(isProxyMode && {
              '@media (min-width: 960px)': {
                ...styles.leftContainer['@media (min-width: 960px)'],
                height: `calc(100vh - ${PROXY_BANNER_HEIGHT}px)`,
              },
            }),
          }}
        >
          <Grid2 container sx={styles.leftHeader} flexWrap="wrap">
            <Grid2
              size={{ xs: 0, md: 8 }}
              display="flex"
              justifyContent="start"
              alignItems="center"
              flexWrap="nowrap"
              sx={styles.actionButtonsGrid}
            >
              <Button
                sx={{
                  ...saveButtonStyles.buttonChange,
                  mr: 1,
                  padding: '10px 16px',
                  height: '44px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ...styles.buttonsDisplayNoneSp,
                }}
                disabled={saving}
                onClick={handleProcessButton}
              >
                <Typography sx={saveButtonStyles.textColorChange}>{buttonText()}</Typography>
              </Button>
              <Button
                sx={{
                  ...secondaryButtonBackgroundColor,
                  mr: 1,
                  padding: '10px 16px',
                  height: '44px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ...styles.buttonsDisplayNoneSp,
                }}
                disabled={saving}
                onClick={(): void => void handleSave(processingStatus.draft)}
              >
                <Typography sx={styles.draftButtonText}>下書き保存</Typography>
              </Button>
              <Tooltip title="再読取">
                <Button
                  sx={{
                    ...tertiaryButtonBackgroundColor,
                    mr: 1,
                    padding: '10px 16px',
                    height: '44px',
                    minWidth: '32px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    ...styles.buttonsDisplayNoneSp,
                  }}
                  disabled={isReOcrProcessing}
                  onClick={handleReOcr}
                >
                  <ReplayIcon />
                  <Typography
                    sx={{
                      ...boldMdGray,
                      display: 'none',
                      '@media (min-width: 2100px)': { display: 'inline' },
                    }}
                  >
                    再読取
                  </Typography>
                </Button>
              </Tooltip>
              <Tooltip title="仕分け変更">
                <Button
                  sx={{
                    ...tertiaryButtonBackgroundColor,
                    mr: 1,
                    padding: '10px 16px',
                    height: '44px',
                    minWidth: '32px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    ...styles.buttonsDisplayNoneSp,
                  }}
                  disabled={reclassifyLoading}
                  onClick={(): void => setReclassifyModalOpen(true)}
                >
                  <SwapHorizIcon />
                  <Typography
                    sx={{
                      ...boldMdGray,
                      display: 'none',
                      '@media (min-width: 2100px)': { display: 'inline' },
                    }}
                  >
                    {reclassifyLoading ? '変更中...' : '仕分け変更'}
                  </Typography>
                </Button>
              </Tooltip>
              <Button
                sx={{
                  ...tertiaryButtonBackgroundColor,
                  mr: 1,
                  padding: '10px 16px',
                  height: '44px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ...styles.buttonsDisplayNoneSp,
                }}
                disabled={saving}
                onClick={(): void => setFileExportDialogOpen(true)}
              >
                <FileDownloadOutlinedIcon />
                <Typography
                  sx={{
                    ...boldMdGray,
                    display: 'none',
                    '@media (min-width: 2100px)': { display: 'inline' },
                  }}
                >
                  出力
                </Typography>
              </Button>
              <Button
                sx={{
                  ...tertiaryButtonBackgroundColor,
                  mr: 1,
                  padding: '10px 16px',
                  height: '44px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  ...styles.buttonsDisplayNoneSp,
                }}
                onClick={(): void => setIsModalOpen(true)}
              >
                <DeleteOutlinedIcon />
                <Typography
                  sx={{
                    ...boldMdGray,
                    display: 'none',
                    '@media (min-width: 2100px)': { display: 'inline' },
                  }}
                >
                  削除
                </Typography>
              </Button>
            </Grid2>
            <Grid2
              size={{ xs: 12, md: 4 }}
              display="flex"
              justifyContent="end"
              alignItems="center"
              sx={styles.navButtonsGrid}
            >
              {prevResultId && (
                <Button sx={styles.KeyboardArrowLeftButton} onClick={handlePrevResult}>
                  <KeyboardArrowLeftIcon sx={styles.leftHeaderIconSize} />
                  <Typography sx={styles.textCss}>前へ</Typography>
                </Button>
              )}
              {nextResultId && (
                <Button sx={styles.KeyboardArrowRightButton} onClick={handleNextResult}>
                  <Typography sx={styles.textCss}>次へ</Typography>
                  <KeyboardArrowRightIcon sx={styles.leftHeaderIconSize} />
                </Button>
              )}
              <Button
                sx={styles.closeButton}
                onClick={(): void => {
                  // 遷移元が戻り先を指定していれば優先する（例: メール取り込みの詳細から遷移）
                  const backTo = (location.state as { backTo?: string } | null)?.backTo;
                  navigate(backTo ? backTo : '/ai-ocr/free');
                }}
              >
                <CloseIcon sx={styles.leftHeaderIconSize} />
              </Button>
            </Grid2>
          </Grid2>

          {/* 作成者・日時情報 */}
          <Stack
            direction="column"
            sx={{ ...styles.staffName, alignItems: 'flex-end' }}
            spacing={1}
          >
            <Stack
              direction="row"
              spacing={3}
              sx={{ justifyContent: 'flex-end', alignItems: 'center', rowGap: '8px' }}
              flexWrap="wrap"
            >
              {/* OCR文字数 */}
              {((): JSX.Element | null => {
                const tokenUsage = resultRecord?.token_usage as Record<string, number> | undefined;
                if (tokenUsage?.total_char_count === undefined) return null;
                return (
                  <Typography sx={regularSm} textAlign="center">
                    OCR文字数：{tokenUsage.total_char_count.toLocaleString()}文字
                  </Typography>
                );
              })()}
              {/* 消費枚数 */}
              {((): JSX.Element | null => {
                const tokenUsage = resultRecord?.token_usage as Record<string, number> | undefined;
                if (tokenUsage?.calculated_usage_count === undefined) return null;
                const billableExecutions = ocrExecutions.filter(e => !e.is_proxy);
                const reOcrCount = billableExecutions.filter(e => {
                  if (e.execution_type === ocrExecutionType.initial) return false;
                  const usage =
                    (e.token_usage as Record<string, number>)?.calculated_usage_count ?? 0;
                  return usage > 0;
                }).length;
                const totalUsage =
                  billableExecutions.length === 0
                    ? tokenUsage.calculated_usage_count
                    : reOcrCount > 0
                      ? billableExecutions.reduce((sum, e) => {
                          const usage =
                            (e.token_usage as Record<string, number>)?.calculated_usage_count ?? 0;
                          return sum + usage;
                        }, 0)
                      : tokenUsage.calculated_usage_count;
                return (
                  <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                    <Typography sx={regularSm}>
                      消費枚数：{totalUsage}枚{reOcrCount > 0 && `（再読取${reOcrCount}回分含む）`}
                    </Typography>
                    <Tooltip title="消費枚数とは？" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(): void => {
                          window.open(HELP_FAQ_CONSUMPTION_COUNT, '_blank');
                        }}
                        sx={{ padding: 0 }}
                      >
                        <HelpOutlineIcon sx={{ fontSize: 16, color: 'gray' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })()}
              <Typography sx={regularSm} textAlign="center">
                アップロード日時：
                {resultRecord?.created_at
                  ? new Date((resultRecord.created_at as Timestamp).seconds * 1000).toLocaleString(
                      'ja-JP',
                    )
                  : ''}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={regularSm} textAlign="center">
                  作成者：
                </Typography>
                <Avatar
                  alt={createUser}
                  src={icon || NoAccountIcon}
                  sx={{ width: 24, height: 24 }}
                />
                <Typography sx={regularSm} textAlign="center">
                  {createUser}
                </Typography>
              </Stack>
              {/* 担当者（#2642）作成者の隣に表示し、編集アイコンからモーダルで変更可能 */}
              <Stack direction="row" spacing={1} alignItems="center">
                {((): ReactElement => {
                  const { displayName, displayIconUrl } = resolveAssigneeDisplay(
                    resultRecord?.assignee_id as string | undefined,
                    resultRecord?.assignee_name as string | undefined,
                    resultRecord?.assignee_icon_url as string | null | undefined,
                    assigneeOptions,
                  );
                  return (
                    <>
                      <Typography sx={regularSm} textAlign="center">
                        {assigneeLabels.label}：
                      </Typography>
                      <Avatar
                        alt={displayName}
                        src={displayIconUrl ?? NoAccountIcon}
                        sx={{ width: 24, height: 24 }}
                      />
                      <Typography sx={regularSm} textAlign="center">
                        {displayName}
                      </Typography>
                      <Tooltip title={assigneeLabels.changeAction}>
                        <IconButton
                          size="small"
                          onClick={(): void => setAssigneeDialogOpen(true)}
                          sx={{ padding: '2px' }}
                        >
                          <EditIcon sx={{ fontSize: '14px' }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  );
                })()}
              </Stack>
            </Stack>
            {/* 2行目：運営スタッフかつ詳細モードON のときのみ表示（#3315）。
                パターンOCRの結果画面と同じく、コスト・処理時間は1行目から分けて並べる。 */}
            {isDetailView && (
              <>
                {/* OCRの流れ（OCR → 値取得 → 構造化）。パターンOCRの結果画面と同じ並び（#3375）。
                    どのモデルで読み取ったかを運営が確認できるようにする。 */}
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ justifyContent: 'flex-end', alignItems: 'center', rowGap: '8px' }}
                  flexWrap="wrap"
                >
                  {((): JSX.Element | null => {
                    const tokenUsage = resultRecord?.token_usage as DocumentData | undefined;
                    if (!tokenUsage) return null;
                    const readModel = getModelNameDisplay(tokenUsage.gemini_ocr?.model_name);
                    const structuredModel = getModelNameDisplay(
                      tokenUsage.gemini_structured?.model_name,
                    );
                    return (
                      <>
                        <Typography sx={regularSm} textAlign="center">
                          OCR：{getOcrEngineDisplayName(tokenUsage.azure_vision?.engine)}
                        </Typography>
                        {readModel && (
                          <Typography sx={regularSm} textAlign="center">
                            値取得：{readModel}
                          </Typography>
                        )}
                        {structuredModel && (
                          <Typography sx={regularSm} textAlign="center">
                            構造化：{structuredModel}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Stack>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ justifyContent: 'flex-end', alignItems: 'center', rowGap: '8px' }}
                  flexWrap="wrap"
                >
                  {/* コスト（ツールチップで内訳表示） */}
                  <OcrCostDisplay tokenUsage={resultRecord?.token_usage as DocumentData} />
                  {/* 処理時間（運営スタッフのみ表示） */}
                  {((): JSX.Element | null => {
                    const tokenUsage = resultRecord?.token_usage as
                      | Record<string, number>
                      | undefined;
                    if (tokenUsage?.processing_time_ms === undefined) return null;
                    const diffSec = Math.floor(tokenUsage.processing_time_ms / 1000);
                    const timeText =
                      diffSec < 60
                        ? `${diffSec}秒`
                        : diffSec < 3600
                          ? `${Math.floor(diffSec / 60)}分${diffSec % 60}秒`
                          : `${Math.floor(diffSec / 3600)}時間${Math.floor((diffSec % 3600) / 60)}分`;
                    return (
                      <Typography sx={regularSm} textAlign="center">
                        処理時間：{timeText}
                      </Typography>
                    );
                  })()}
                </Stack>
              </>
            )}
          </Stack>

          {/* 読取結果：汎用レンダラー */}
          <Box sx={styles.deliveryMainInfo}>
            {Object.keys(editData).length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                読取データがありません
              </Typography>
            ) : (
              renderOcrNode(editData, [], 0)
            )}
          </Box>
        </Grid2>
      </Grid2>

      {/* SP用フッターボタン */}
      <Box sx={styles.footerButtons}>
        <Button
          sx={[saveButtonStyles.buttonChange, { height: '44px' }]}
          disabled={saving}
          onClick={handleProcessButton}
        >
          <Typography sx={saveButtonStyles.textColorChange}>{buttonText()}</Typography>
        </Button>
        <Box sx={styles.footerTwoButtons}>
          <Button
            sx={{ ...secondaryButtonBackgroundColor, flexGrow: 4, height: '44px' }}
            onClick={(): void => void handleSave(processingStatus.draft)}
            disabled={saving}
          >
            <Typography sx={styles.draftButtonText}>下書き保存</Typography>
          </Button>
          <Button
            sx={{ ...secondaryButtonBackgroundColor, flexGrow: 4, height: '44px' }}
            onClick={(): void => setFileExportDialogOpen(true)}
            disabled={saving}
          >
            <Typography sx={styles.draftButtonText}>出力</Typography>
          </Button>
        </Box>
      </Box>

      {/* 担当者変更ダイアログ（#2642）親が保持する候補リストを渡して二重フェッチを防ぐ */}
      <AssigneeChangeDialog
        open={assigneeDialogOpen}
        onClose={(): void => setAssigneeDialogOpen(false)}
        mode="single"
        targetResultIds={resultId ? [resultId] : []}
        currentAssigneeId={resultRecord?.assignee_id as string | undefined}
        targetLabel={fileName}
        companyId={effectiveCompanyId ?? user?.company_id ?? ''}
        options={assigneeOptions}
        onSuccess={handleAssigneeChangeSuccess}
        onError={handleAssigneeChangeError}
      />

      <DeleteModal
        open={isModalOpen}
        onClose={(): void => setIsModalOpen(false)}
        onDelete={handleDelete}
        title="ファイル"
      />

      <FileExportDialog
        open={fileExportDialogOpen}
        onClose={(): void => setFileExportDialogOpen(false)}
        onExport={handleFileExport}
        pattern={null}
        selectedTab={
          typeof resultRecord?.processing_status === 'number'
            ? resultRecord.processing_status
            : processingStatus.completed
        }
        defaultFileName={
          fileName
            ? `${fileName.replace(/\.[^/.]+$/, '')}_${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}`
            : undefined
        }
      />

      <LoadingModal
        open={isReOcrProcessing}
        progress={reOcrProgress}
        message={reclassifyLoading ? '仕分け変更しています...' : '再読取しています...'}
      />

      <OcrLimitDialog open={limitDialogOpen} onClose={(): void => setLimitDialogOpen(false)} />

      <PatternSelectModal
        open={reclassifyModalOpen}
        onClose={(): void => {
          setReclassifyModalOpen(false);
        }}
        onSelect={handleReclassifySelect}
        currentPatternId={patternId}
        companyId={effectiveCompanyId || ''}
        usesCredit={calculateOcrLimitInfo(company, baseRemainingCount).remainingCount <= 0}
        hideWarning={isProxyMode}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </ThemeProvider>
  );
};

export default FreeAiOcrResult;
